import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";
import type { PatchBrandingRequest, PatchFunctionsRequest, PatchSettingsRequest } from "@performyard/service-contracts";

import {
	adminSettingsHomeBrandingQuery,
	adminSettingsHomeFunctionsQuery,
	adminSettingsHomeSeatUsageQuery,
	adminSettingsHomeSettingsQuery,
} from "../../queries";
import {
	patchAdminSettingsHomeBranding,
	patchAdminSettingsHomeFunctions,
	patchAdminSettingsHomeSettings,
	postAdminSettingsHomeBrandingReset,
	postAdminSettingsHomeLogo,
} from "../../services";

const ADMIN_HOME_KEY = ["admin-settings-home"];

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
	void queryClient.invalidateQueries({ queryKey: ADMIN_HOME_KEY });
	void queryClient.invalidateQueries({ queryKey: ["company-settings"] });
	void queryClient.invalidateQueries({ queryKey: ["navigation-config"] });
	void queryClient.invalidateQueries({ queryKey: ["side-nav-themes"] });
};

export const useAdminSettingsHomeSeatUsage = (companyId?: string) =>
	useQuery(adminSettingsHomeSeatUsageQuery(companyId));

export const useAdminSettingsHomeBranding = (companyId?: string) => useQuery(adminSettingsHomeBrandingQuery(companyId));

export const useAdminSettingsHomeFunctions = (companyId?: string) =>
	useQuery(adminSettingsHomeFunctionsQuery(companyId));

export const useAdminSettingsHomeSettings = (companyId?: string) => useQuery(adminSettingsHomeSettingsQuery(companyId));

export const useUpdateAdminSettingsHomeBranding = (onSuccess?: () => void, companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: PatchBrandingRequest) => patchAdminSettingsHomeBranding(body, companyId),
		onSuccess: () => {
			invalidateAll(queryClient);
			toast.success("Branding updated");
			onSuccess?.();
		},
		meta: { errorMessage: "Unable to update branding." },
	});
};

export const useUploadAdminSettingsHomeLogo = (onSuccess?: () => void, companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (file: File) => postAdminSettingsHomeLogo(file, companyId),
		onSuccess: () => {
			invalidateAll(queryClient);
			toast.success("Logo updated");
			onSuccess?.();
		},
		meta: { errorMessage: "Unable to upload logo." },
	});
};

export const useResetAdminSettingsHomeBranding = (onSuccess?: () => void, companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => postAdminSettingsHomeBrandingReset(companyId),
		onSuccess: () => {
			invalidateAll(queryClient);
			toast.success("Branding reset");
			onSuccess?.();
		},
		meta: { errorMessage: "Unable to reset branding." },
	});
};

export const useUpdateAdminSettingsHomeFunctions = (onSuccess?: () => void, companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: PatchFunctionsRequest) => patchAdminSettingsHomeFunctions(body, companyId),
		onSuccess: () => {
			invalidateAll(queryClient);
			toast.success("Features updated");
			onSuccess?.();
		},
		meta: { errorMessage: "Unable to update features." },
	});
};

export const useUpdateAdminSettingsHomeSettings = (onSuccess?: () => void, companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: PatchSettingsRequest) => patchAdminSettingsHomeSettings(body, companyId),
		onSuccess: () => {
			invalidateAll(queryClient);
			toast.success("Company settings updated");
			onSuccess?.();
		},
		meta: { errorMessage: "Unable to update company settings." },
	});
};
